import { AI_ASSISTANT } from "@/config/constants";
import { CONTACT_VISIBILITY, type ContactVisibility } from "@/types/role";
import { WORKSPACE_ROLE, type WorkspaceRole } from "@/types/user";

const ECHO = AI_ASSISTANT.name;

// Every key the app gates on or has wording for. What each needs, what a
// limited role can't hold and what each type starts with come from the backend
// (`PermissionRules`); a key it adds before the wording lands shows under "Other".
export const PERMISSIONS = [
  "contacts.view",
  "contacts.edit",
  "contacts.delete",
  "contacts.import",
  "contacts.export",
  "conversations.assign",
  "conversations.delete",
  "appointments.view",
  "appointments.edit",
  "appointments.delete",
  "payments.edit",
  "payments.delete",
  "orders.view",
  "orders.edit",
  "orders.delete",
  "tickets.view",
  "tickets.edit",
  "tickets.delete",
  "channels.manage",
  "channels.delete",
  "channels.connect",
  "broadcasts.view",
  "broadcasts.edit",
  "broadcasts.delete",
  "workflows.view",
  "workflows.edit",
  "workflows.delete",
  "ads.view",
  "ads.edit",
  "ads.delete",
  "ads.connect",
  "reports.view",
  "reports.amount",
  "reports.export",
  "billing.view",
  "plan.manage",
  "credits.buy",
  "treatments.manage",
  "treatments.delete",
  "store.manage",
  "store.delete",
  "echo.view",
  "echo.edit",
  "echo.knowledge",
  "echo.suggestions",
  "echo.mode",
  "echo.delete",
  "replies.manage",
  "replies.delete",
  "views.create",
  "views.share",
  "inbox.manage",
  "inbox.delete",
  "teams.manage",
  "teams.delete",
  "integrations.manage",
  "integrations.connect",
  "integrations.delete",
  "workspace.settings",
  "members.manage",
  "members.delete",
  "roles.manage",
  "developer.manage",
  "supplies.view",
  "supplies.order",
  "supplies.manage",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type PermissionItem = {
  key: Permission;
  label: string;
  description?: string;
};

export type PermissionSection = {
  key: string;
  label: string;
  description: string;
  permissions: PermissionItem[];
};

export const PERMISSION_SECTIONS: PermissionSection[] = [
  {
    key: "customers",
    label: "Customers",
    description: "Contacts and the work around them.",
    permissions: [
      {
        key: "contacts.view",
        label: "Can view contacts",
        description: "Profiles, fields, tags and history",
      },
      {
        key: "contacts.edit",
        label: "Can add and change contacts",
      },
      {
        key: "contacts.delete",
        label: "Can delete and merge contacts",
        description: "Also their call logs, delivery addresses and segments",
      },
      {
        key: "contacts.import",
        label: "Can import contacts",
        description: "Upload a list to add or update contacts",
      },
      {
        key: "contacts.export",
        label: "Can export contacts",
        description: "Download contact lists as a file",
      },
      {
        key: "conversations.assign",
        label: "Can assign conversations",
        description: "Hand conversations to teammates and teams",
      },
      {
        key: "conversations.delete",
        label: "Can delete conversations",
        description: "The conversation and all its messages",
      },
      {
        key: "appointments.view",
        label: "Can view appointments",
        description: "Bookings, outcomes and follow-ups",
      },
      {
        key: "appointments.edit",
        label: "Can book, move and record outcomes",
      },
      {
        key: "appointments.delete",
        label: "Can delete appointments",
      },
      {
        key: "payments.edit",
        label: "Can record payments",
        description:
          "And fix or delete one they recorded, until the end of that day",
      },
      {
        key: "payments.delete",
        label: "Can edit or delete anyone's payment",
        description: "From any day",
      },
      {
        key: "orders.view",
        label: "Can view orders",
        description: "What was ordered, its status and delivery",
      },
      {
        key: "orders.edit",
        label: "Can create and change orders",
      },
      {
        key: "orders.delete",
        label: "Can delete orders",
      },
      {
        key: "tickets.view",
        label: "Can view tickets",
        description: "Issues raised from conversations",
      },
      {
        key: "tickets.edit",
        label: "Can create, update and close tickets",
      },
      {
        key: "tickets.delete",
        label: "Can delete tickets and comments",
      },
    ],
  },
  {
    key: "outreach",
    label: "Channels and outreach",
    description: "Where messages come from and what goes out.",
    permissions: [
      {
        key: "channels.manage",
        label: "Can manage channels",
        description:
          "Each channel's settings, templates, welcome messages, menus and websites",
      },
      {
        key: "channels.delete",
        label: "Can delete channels",
        description: "Also WhatsApp templates and websites",
      },
      {
        key: "channels.connect",
        label: "Can connect channels",
        description: "Add a new number, page or inbox",
      },
      {
        key: "broadcasts.view",
        label: "Can view broadcasts",
        description: "Messages sent to a segment, and their results",
      },
      {
        key: "broadcasts.edit",
        label: "Can create, schedule and send broadcasts",
      },
      {
        key: "broadcasts.delete",
        label: "Can delete broadcasts",
      },
      {
        key: "workflows.view",
        label: "Can view workflows",
        description: "Automations and their runs",
      },
      {
        key: "workflows.edit",
        label: "Can build workflows and switch them on or off",
      },
      {
        key: "workflows.delete",
        label: "Can archive workflows",
        description: "Archived ones can be restored",
      },
      {
        key: "ads.view",
        label: "Can view ads",
        description: "Ad accounts and the leads they bring",
      },
      {
        key: "ads.edit",
        label: "Can change ads",
        description: "What each ad is linked to, its notes, and syncing",
      },
      {
        key: "ads.delete",
        label: "Can disconnect ad accounts",
      },
      {
        key: "ads.connect",
        label: "Can connect ad accounts",
        description: "Add or reconnect an ad account",
      },
    ],
  },
  {
    key: "reports",
    label: "Reports and billing",
    description: "Numbers, revenue and what the workspace pays for.",
    permissions: [
      {
        key: "reports.view",
        label: "Can open reports",
        description: "Every report page",
      },
      {
        key: "reports.amount",
        label: "Can see amounts",
        description:
          "Appointment value, revenue and payments. Hidden across reports when off",
      },
      {
        key: "reports.export",
        label: "Can export reports",
        description: "Download report data as a file",
      },
      {
        key: "billing.view",
        label: "Can view the billing ledger",
        description: "Invoices and payments from the billing system",
      },
      {
        key: "plan.manage",
        label: "Can change the plan",
        description: "Upgrade, downgrade or cancel",
      },
      {
        key: "credits.buy",
        label: "Can buy credits",
        description: "Top up the credit balance",
      },
    ],
  },
  {
    key: "catalog",
    label: "Catalog",
    description: "What the business offers.",
    permissions: [
      {
        key: "treatments.manage",
        label: "Can manage treatments, doctors and departments",
        description: "Prices, schedules and who provides what",
      },
      {
        key: "treatments.delete",
        label: "Can delete treatments, doctors and departments",
      },
      {
        key: "store.manage",
        label: "Can manage products and discounts",
        description: "Also brands, categories and the storefront",
      },
      {
        key: "store.delete",
        label: "Can delete products and discounts",
        description: "Also brands, categories and the storefront",
      },
    ],
  },
  {
    key: "echo",
    label: ECHO,
    description: `How ${ECHO} is set up for this workspace.`,
    permissions: [
      {
        key: "echo.view",
        label: `Can view how ${ECHO} is set up`,
        description: "Instructions, tone and availability",
      },
      {
        key: "echo.edit",
        label: `Can change ${ECHO}'s instructions, tone and availability`,
      },
      {
        key: "echo.knowledge",
        label: `Can edit what ${ECHO} knows`,
      },
      {
        key: "echo.suggestions",
        label: `Can review ${ECHO}'s suggestions`,
        description: `Approve what ${ECHO} learned from conversations`,
      },
      {
        key: "echo.mode",
        label: `Can turn ${ECHO} on or off`,
        description: "For the whole workspace",
      },
      {
        key: "echo.delete",
        label: `Can delete ${ECHO} and its test cases`,
      },
    ],
  },
  {
    key: "inbox",
    label: "Inbox",
    description: "The tools people use while they reply.",
    permissions: [
      {
        key: "replies.manage",
        label: "Can manage shared saved replies and files",
        description: "Anyone can still save replies for themselves",
      },
      {
        key: "replies.delete",
        label: "Can delete shared saved replies and files",
      },
      {
        key: "views.create",
        label: "Can create their own views",
        description: "Saved inbox filters only they see",
      },
      {
        key: "views.share",
        label: "Can share views with everyone",
      },
      {
        key: "inbox.manage",
        label: "Can manage inbox setup",
        description:
          "Tags, lifecycle stages, fields, closing notes, outcomes, wrap-up, SLA and business hours",
      },
      {
        key: "inbox.delete",
        label: "Can delete tags, stages, fields and other inbox setup",
      },
      {
        key: "teams.manage",
        label: "Can manage teams",
        description: "Create teams and change who is in them",
      },
      {
        key: "teams.delete",
        label: "Can delete teams",
      },
    ],
  },
  {
    key: "workspace",
    label: "Workspace",
    description: "Running the workspace itself.",
    permissions: [
      {
        key: "integrations.manage",
        label: "Can manage integrations",
        description: "Clinic systems, calendars, stores and delivery partners",
      },
      {
        key: "integrations.connect",
        label: "Can connect integrations",
        description:
          "Add a new clinic system, calendar, store or delivery partner",
      },
      {
        key: "integrations.delete",
        label: "Can delete integrations",
      },
      {
        key: "workspace.settings",
        label: "Can change workspace settings",
        description: "Name, timezone and assignment rules",
      },
      {
        key: "members.manage",
        label: "Can manage members",
        description: "Invite people and change their role",
      },
      {
        key: "members.delete",
        label: "Can delete members",
        description: "Takes them out of the workspace",
      },
      {
        key: "roles.manage",
        label: "Can manage roles",
        description: "Create and change roles like this one",
      },
      {
        key: "developer.manage",
        label: "Can manage API keys and webhooks",
        description: "Keys and endpoints that act for the workspace",
      },
    ],
  },
  {
    key: "supplies",
    label: "Supplies",
    description: "Ordering clinic supplies.",
    permissions: [
      {
        key: "supplies.view",
        label: "Can view supplies, stock and orders",
      },
      {
        key: "supplies.order",
        label: "Can order supplies",
        description: "Cart, checkout, agreements and order requests",
      },
      {
        key: "supplies.manage",
        label: "Can manage Supplies",
        description: "Connection, stock counts, item settings and saved cards",
      },
    ],
  },
];

// Pickers and filters read these lists for every role; a "manage" switch is the area's settings.
export const SHARED_LISTS_NOTE =
  "Teammates, teams, channels, tags, the catalog and the other lists show for everyone wherever they pick or filter. A manage switch opens that area's settings and lets them change it.";

// The summary of what a role can do: one line per area, in catalog order.
export const PERMISSION_AREA_LABEL: Record<string, string> = {
  contacts: "Contacts",
  conversations: "Conversations",
  appointments: "Appointments",
  payments: "Payments",
  orders: "Orders",
  tickets: "Tickets",
  channels: "Channels",
  broadcasts: "Broadcasts",
  workflows: "Workflows",
  ads: "Ads",
  reports: "Reports",
  billing: "Billing ledger",
  plan: "Plan",
  credits: "Credits",
  treatments: "Treatments and doctors",
  store: "Store",
  echo: ECHO,
  replies: "Saved replies",
  views: "Views",
  inbox: "Inbox setup",
  teams: "Teams",
  integrations: "Integrations",
  supplies: "Supplies",
  workspace: "Workspace settings",
  members: "Members",
  roles: "Roles",
  developer: "API keys and webhooks",
};

// The verb a summary line uses for a key. Anything missing uses the key's action.
export const PERMISSION_SHORT_LABEL: Partial<Record<Permission, string>> = {
  "appointments.edit": "book and move",
  "payments.edit": "record",
  "payments.delete": "edit or delete anyone's",
  "broadcasts.edit": "send",
  "workflows.edit": "build",
  "workflows.delete": "archive",
  "reports.view": "open",
  "reports.amount": "see amounts",
  "plan.manage": "change",
  "credits.buy": "buy",
  "echo.knowledge": "edit knowledge",
  "echo.suggestions": "review suggestions",
  "echo.mode": "turn on or off",
  "workspace.settings": "change",
  "contacts.edit": "add and change",
};

// The one line beside the Agent or Member pick: what the choice decides.
export const ROLE_TYPE_HINT: Partial<Record<WorkspaceRole, string>> = {
  [WORKSPACE_ROLE.AGENT]: "Works conversations. Can be kept to some contacts.",
  [WORKSPACE_ROLE.MEMBER]: "Runs the clinic. Can be kept to some contacts.",
};

// What a role's type means beyond its permissions.
export const ROLE_TYPE_DESCRIPTION: Partial<Record<WorkspaceRole, string>> = {
  [WORKSPACE_ROLE.ADMIN]:
    "Sees and does everything, including roles and members.",
  [WORKSPACE_ROLE.AGENT]:
    "Works conversations. Alerted about unassigned conversations by default, gets the agent digest, and counts as an agent in reports.",
  [WORKSPACE_ROLE.MEMBER]:
    "Runs the clinic. Gets the team digest with revenue, or the agent one when kept to some contacts, and is not counted as an agent in reports.",
};

export const AGENT_ROLE_NOTE = `Agent roles stick to conversation work. Reports, billing, outreach, the catalog, ${ECHO} setup and settings need a Member role.`;

// The summary's name for the role's contact access.
export const SUMMARY_CONTACTS_AREA = "Sees";

export const ROLE_TYPE_NAME: Record<WorkspaceRole, string> = {
  [WORKSPACE_ROLE.ADMIN]: "Admin",
  [WORKSPACE_ROLE.MEMBER]: "Member",
  [WORKSPACE_ROLE.AGENT]: "Agent",
  [WORKSPACE_ROLE.MARKETING]: "Marketing",
  [WORKSPACE_ROLE.FINANCE]: "Finance",
};

export const CONTACT_VISIBILITY_OPTIONS: Array<{
  value: ContactVisibility;
  label: string;
  description: string;
}> = [
  {
    value: CONTACT_VISIBILITY.ALL,
    label: "All contacts",
    description: "Every contact in the workspace",
  },
  {
    value: CONTACT_VISIBILITY.TEAMS,
    label: "Their teams' contacts",
    description: "Assigned to them, or to a team they belong to",
  },
  {
    value: CONTACT_VISIBILITY.ASSIGNED,
    label: "Only assigned to them",
    description: "Contacts assigned to them personally",
  },
];

export const CONTACT_VISIBILITY_LABEL: Record<ContactVisibility, string> =
  Object.fromEntries(
    CONTACT_VISIBILITY_OPTIONS.map((option) => [option.value, option.label]),
  ) as Record<ContactVisibility, string>;
